import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { getDb } from "./firebase";

// Mirrors the backend's `config/buyFlags` doc shape: one boolean pair
// (web/mobile) per product. Only the `.web` leaf matters here.
export type BuyFlags = {
  rca: boolean;
  travel: boolean;
  malpraxis: boolean;
  pad: boolean;
  roadAssist: boolean;
  casco: boolean;
  cmr: boolean;
  optionalHouseInsurance: boolean;
};
export type BuyFlagsStatus = "loading" | "ready" | "error";
export type UseBuyFlagsState = { flags: BuyFlags; status: BuyFlagsStatus };

// Fail-closed placeholder: while loading/error, every flag reads `false` so
// a consumer that forgets to check `status` can't accidentally allow a buy
// flow. Real consumers must gate on `status`, not on these values.
const FAIL_CLOSED: BuyFlags = {
  rca: false,
  travel: false,
  malpraxis: false,
  pad: false,
  roadAssist: false,
  casco: false,
  cmr: false,
  optionalHouseInsurance: false,
};

function readWeb(data: Record<string, Record<string, unknown>> | undefined): BuyFlags {
  const leaf = (p: string) => data?.[p]?.web === true;
  return {
    rca: leaf("rca"),
    travel: leaf("travel"),
    malpraxis: leaf("malpraxis"),
    pad: leaf("pad"),
    roadAssist: leaf("roadAssist"),
    casco: leaf("casco"),
    cmr: leaf("cmr"),
    optionalHouseInsurance: leaf("optionalHouseInsurance"),
  };
}

// Subscribes to the global `config/buyFlags` doc (no auth dependency — the
// flags gate product availability for everyone, signed in or not).
export function useBuyFlags(): UseBuyFlagsState {
  const [state, setState] = useState<UseBuyFlagsState>({ flags: FAIL_CLOSED, status: "loading" });

  useEffect(() => {
    const unsub = onSnapshot(
      doc(getDb(), "config", "buyFlags"),
      (snap) => setState({ flags: readWeb(snap.exists() ? (snap.data() as never) : undefined), status: "ready" }),
      () => setState({ flags: FAIL_CLOSED, status: "error" }),
    );
    return unsub;
  }, []);

  return state;
}
